import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Loading from '../components/common/Loading';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import { Plus, Edit, Trash2, Store, Search, MapPin, Phone, Mail, Percent } from 'lucide-react';
import { partnerService } from '../services/partnerService';
import { Partner } from '../types';
import { formatCNPJ, formatPhone } from '../utils/formatters';
import { getImageUrl } from '../utils/imageHelper';

export const Partners: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingPartner, setDeletingPartner] = useState<Partner | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchPartners();
  }, [currentPage, search]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await partnerService.getAll(currentPage, search);
      setPartners(response.data);
      setTotalPages(response.last_page);
    } catch (err: any) {
      setError('Erro ao carregar parceiros');
      console.error('Partners fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPartner) return;

    try {
      await partnerService.delete(Number(deletingPartner.id));
      setSuccess('Parceiro excluído com sucesso!');
      setShowDeleteModal(false);
      setDeletingPartner(null);
      fetchPartners();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir parceiro');
      setShowDeleteModal(false);
      setDeletingPartner(null);
    }
  };

  const handleToggleActive = async (partner: Partner) => {
    try {
      await partnerService.toggleActive(Number(partner.id));
      setSuccess(`Parceiro ${partner.active ? 'desativado' : 'ativado'} com sucesso!`);
      fetchPartners();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar parceiro');
    }
  };

  const openDeleteModal = (partner: Partner) => {
    setDeletingPartner(partner);
    setShowDeleteModal(true);
  };

  if (loading && partners.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Parceiros</h1>
            <p className="text-gray-600 mt-2">
              Gerencie os estabelecimentos parceiros que oferecem descontos
            </p>
          </div>
          <Button
            onClick={() => navigate('/parceiros/novo')}
            className="flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Novo Parceiro
          </Button>
        </div>

        {/* Alerts */}
        {error && (
          <Alert type="error" className="mb-6" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert type="success" className="mb-6" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Search */}
        <div className="mb-6">
          <Input
            type="text"
            placeholder="Buscar por nome, CNPJ ou cidade..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            icon={<Search className="h-5 w-5" />}
          />
        </div>

        {/* Content */}
        {partners.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nenhum parceiro cadastrado
              </h3>
              <p className="text-gray-600 mb-6">
                Comece adicionando o primeiro parceiro ao sistema
              </p>
              <Button
                onClick={() => navigate('/parceiros/novo')}
                className="flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Adicionar Parceiro
              </Button>
            </div>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {partners.map((partner) => (
                <Card key={partner.id} className="hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    {/* Header com Logo */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3 flex-1">
                        {partner.logo_url && getImageUrl(partner.logo_url) && (
                          <img
                            src={getImageUrl(partner.logo_url)!}
                            alt={partner.nome_fantasia || partner.name}
                            className="w-12 h-12 object-contain rounded"
                            onError={(e) => {
                              // Esconder imagem se falhar ao carregar
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1">
                            {partner.nome_fantasia || partner.name}
                          </h3>
                          {partner.razao_social && partner.razao_social !== partner.nome_fantasia && (
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {partner.razao_social}
                            </p>
                          )}
                          {partner.category && (
                            <p className="text-sm text-gray-600 mt-1">{partner.category}</p>
                          )}
                        </div>
                      </div>
                      <Badge variant={partner.active || partner.ativo ? 'success' : 'danger'}>
                        {partner.active || partner.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>

                    {/* Informações */}
                    <div className="space-y-2 mb-4 text-sm text-gray-600">
                      <div className="flex items-start gap-2">
                        <Store className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-1">{formatCNPJ(partner.cnpj)}</span>
                      </div>

                      <div className="flex items-start gap-2">
                        <Phone className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{formatPhone(partner.telefone || partner.phone)}</span>
                      </div>

                      {partner.email && (
                        <div className="flex items-start gap-2">
                          <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-1">{partner.email}</span>
                        </div>
                      )}

                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2">
                          {partner.cidade && partner.estado
                            ? `${partner.cidade} - ${partner.estado}`
                            : partner.address || partner.endereco}
                        </span>
                      </div>

                      {(partner.desconto_percentual || partner.discount_percentage) && (
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                          <Percent className="h-5 w-5 text-green-600" />
                          <span className="text-green-600 font-semibold">
                            {partner.desconto_percentual || partner.discount_percentage}% de desconto
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-gray-200">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => navigate(`/parceiros/${partner.id}/editar`)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant={partner.active || partner.ativo ? 'outline' : 'outline'}
                        size="sm"
                        onClick={() => handleToggleActive(partner)}
                        title={partner.active || partner.ativo ? 'Desativar' : 'Ativar'}
                      >
                        {partner.active || partner.ativo ? 'Desativar' : 'Ativar'}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => openDeleteModal(partner)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deletingPartner && (
          <Modal
            isOpen={true}
            onClose={() => {
              setShowDeleteModal(false);
              setDeletingPartner(null);
            }}
            title="Confirmar Exclusão"
          >
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                Tem certeza que deseja excluir o parceiro{' '}
                <strong>{deletingPartner.nome_fantasia || deletingPartner.name}</strong>?
                Esta ação não pode ser desfeita.
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingPartner(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button variant="danger" onClick={handleDelete}>
                  Excluir
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};
